/**
 * Collections API
 */

import { api, type ApiResponse, type ApiListResponse } from '@/lib/api';

// ============== Types ==============

export interface CollectionMeta {
    collection: string;
    icon?: string | null;
    note?: string | null;
    display_template?: string | null;
    hidden?: boolean;
    singleton?: boolean;
    translations?: Record<string, string>[] | null;
    archive_field?: string | null;
    archive_value?: string | null;
    unarchive_value?: string | null;
    archive_app_filter?: boolean;
    sort_field?: string | null;
    accountability?: 'all' | 'activity' | null;
    color?: string | null;
    item_duplication_fields?: string[] | null;
    sort?: number | null;
    group?: string | null;
    collapse?: 'open' | 'closed' | 'locked';
    preview_url?: string | null;
    versioning?: boolean;
}

export interface Collection {
    collection: string;
    meta: CollectionMeta | null;
    schema: {
        name: string;
        comment?: string | null;
    } | null;
}

export interface CreateCollectionPayload {
    collection: string;
    meta?: Partial<CollectionMeta>;
    schema?: {
        name?: string;
        comment?: string | null;
    } | null;
    fields?: Array<{
        field: string;
        type: string;
        schema?: Record<string, unknown>;
        meta?: Record<string, unknown>;
    }>;
}

export type UpdateCollectionPayload = Partial<CollectionMeta>;

// ============== API Functions ==============

export async function getCollections(): Promise<Collection[]> {
    const response = await api.get<ApiListResponse<Collection>>('/collections');
    return response.data ?? [];
}

export async function getCollection(collection: string): Promise<Collection> {
    const response = await api.get<ApiResponse<Collection>>(`/collections/${collection}`);
    return response.data;
}

export async function createCollection(payload: CreateCollectionPayload): Promise<Collection> {
    const response = await api.post<ApiResponse<Collection>>('/collections', payload);
    return response.data;
}

export async function updateCollection(
    collection: string,
    payload: UpdateCollectionPayload
): Promise<Collection> {
    // Backend schema stores collection meta as direct columns, not nested under "meta"
    const response = await api.patch<ApiResponse<Collection>>(`/collections/${collection}`, payload);
    return response.data;
}

export async function deleteCollection(collection: string): Promise<void> {
    await api.delete(`/collections/${collection}`);
}

export async function renameFolder(collection: string, newName: string): Promise<Collection> {
    const response = await api.post<ApiResponse<Collection>>(`/collections/${collection}/rename`, {
        newName,
    });
    return response.data;
}
